import React, { useEffect, useRef, useState, useCallback } from "react";
import { Camera, MapPin, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from "lucide-react";

import { AppShell } from "src/components/layout/AppShell";
import { Button } from "src/components/ui/button";
import { useToast } from "src/components/ui/toast";
import { fetchToday, markAttendance } from "src/features/attendance/api/attendanceApi";
import { matchGeofence, nearestGeofence } from "src/features/attendance/utils/geo";

function formatTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function AttendancePage() {
  const { toast } = useToast();
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [today, setToday] = useState(null);
  const [geofences, setGeofences] = useState([]);
  const [position, setPosition] = useState(null); // { lat, lng, accuracy }
  const [locating, setLocating] = useState(true);
  const [locError, setLocError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [selfie, setSelfie] = useState(null); // { blob, url }
  const [submitting, setSubmitting] = useState(false);

  const loadToday = useCallback(async () => {
    try {
      const data = await fetchToday();
      setToday(data);
      setGeofences(data.geofences || []);
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Couldn't load attendance status." });
    }
  }, [toast]);

  // Geolocation
  const locate = useCallback(() => {
    setLocating(true);
    setLocError(null);
    if (!navigator.geolocation) {
      setLocError("Geolocation isn't supported on this device.");
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        setLocating(false);
      },
      (err) => {
        setLocError(
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied. Enable it to mark attendance."
            : "Couldn't determine your location. Try again."
        );
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, []);

  // Camera
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch {
      setCameraReady(false);
      toast({ variant: "destructive", title: "Camera", description: "Couldn't access the camera." });
    }
  }, [toast]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    loadToday();
    locate();
    startCamera();
    return () => stopCamera();
  }, [loadToday, locate, startCamera, stopCamera]);

  function capture() {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) setSelfie({ blob, url: URL.createObjectURL(blob) });
      },
      "image/jpeg",
      0.85
    );
  }

  function retake() {
    if (selfie?.url) URL.revokeObjectURL(selfie.url);
    setSelfie(null);
  }

  const inside = position ? matchGeofence(position.lat, position.lng, geofences) : null;
  const nearest = position ? nearestGeofence(position.lat, position.lng, geofences) : null;

  // Which kind to mark next: check_in until done, then check_out.
  const nextKind = today?.check_in ? "check_out" : "check_in";
  const alreadyDone = today?.check_in && today?.check_out;

  async function submit() {
    if (!position || !selfie) return;
    setSubmitting(true);
    try {
      await markAttendance({
        kind: nextKind,
        latitude: position.lat,
        longitude: position.lng,
        accuracyMeters: position.accuracy,
        selfieBlob: selfie.blob,
      });
      toast({ title: "Marked", description: `Your ${nextKind.replace("_", "-")} is recorded.` });
      retake();
      await loadToday();
    } catch (error) {
      const data = error.response?.data;
      toast({
        variant: "destructive",
        title: data?.status === "rejected" ? "Out of range" : "Couldn't mark",
        description: data?.message || "Something went wrong.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = !!position && !!selfie && !!inside && !alreadyDone && !submitting;

  return (
    <AppShell>
      <div className="mx-auto max-w-md p-6">
        <h1 className="text-[20px] font-semibold text-slate-900">Attendance</h1>
        <p className="mb-4 text-[13px] text-slate-500">
          {new Date(today?.date || Date.now()).toLocaleDateString([], {
            weekday: "long",
            day: "numeric",
            month: "short",
          })}
        </p>

        {/* Camera / selfie */}
        <div className="relative mb-3 aspect-[4/3] overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
          {selfie ? (
            <img src={selfie.url} alt="Attendance selfie" className="h-full w-full object-cover" />
          ) : (
            <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />
          )}
          {!selfie && !cameraReady && (
            <div className="absolute inset-0 flex items-center justify-center text-slate-400">
              <Camera className="h-8 w-8" />
            </div>
          )}
        </div>

        <div className="mb-4 flex gap-2">
          {selfie ? (
            <Button variant="outline" onClick={retake} className="flex-1">
              <RefreshCw className="mr-1.5 h-4 w-4" />
              Retake
            </Button>
          ) : (
            <Button variant="outline" onClick={capture} disabled={!cameraReady} className="flex-1">
              <Camera className="mr-1.5 h-4 w-4" />
              Capture selfie
            </Button>
          )}
        </div>

        {/* Location status */}
        <div className="mb-3 flex items-center gap-2 text-[13px] text-slate-600">
          <MapPin className="h-4 w-4" />
          {locating ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Detecting your location…
            </span>
          ) : locError ? (
            <span className="text-rose-600">{locError}</span>
          ) : (
            <span>
              Location detected · accuracy {Math.round(position?.accuracy || 0)} m
            </span>
          )}
          {!locating && (
            <button onClick={locate} className="ml-auto text-[12px] text-[#4f39f6]">
              Refresh
            </button>
          )}
        </div>

        {/* In/out of zone banner */}
        {position && (
          inside ? (
            <div className="mb-4 flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2.5 text-[13px] text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              You're within “{inside.geofence.name}”.
            </div>
          ) : (
            <div className="mb-4 flex items-start gap-2 rounded-md bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {nearest
                  ? `You need to be within “${nearest.geofence.name}” to mark your attendance (${Math.round(
                      nearest.distance
                    )} m away).`
                  : "No attendance location has been configured yet."}
              </span>
            </div>
          )
        )}

        {/* Action */}
        {alreadyDone ? (
          <div className="rounded-md bg-slate-50 px-3 py-2.5 text-center text-[13px] text-slate-500">
            You've completed today's attendance.
          </div>
        ) : (
          <Button onClick={submit} disabled={!canSubmit} className="w-full">
            {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Mark {nextKind === "check_in" ? "check-in" : "check-out"}
          </Button>
        )}

        {/* Today summary */}
        <div className="mt-5 flex justify-between border-t border-slate-100 pt-4 text-[13px] text-slate-600">
          <span>
            Check-in <span className="text-slate-900">{formatTime(today?.check_in?.recorded_at)}</span>
          </span>
          <span>
            Check-out <span className="text-slate-900">{formatTime(today?.check_out?.recorded_at)}</span>
          </span>
        </div>
      </div>
    </AppShell>
  );
}
